import React from 'react';
import { Shield, Eye, Lock, FileText, Mail, Phone } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center px-4 py-2 bg-gold-100 rounded-full mb-6">
            <Shield className="h-4 w-4 text-gold-600 mr-2" />
            <span className="text-gold-700 text-sm font-semibold">Privacy Policy</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-navy-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            How HULA CAPITAL LIMITED collects, uses, and protects your personal information
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: December 2024
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center">
                <Eye className="h-6 w-6 text-gold-600 mr-2" />
                Introduction
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                HULA CAPITAL LIMITED ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our financial services.
              </p>
              <div className="bg-gold-50 p-4 rounded-lg border-l-4 border-gold-500">
                <p className="text-sm text-gray-700">
                  <strong>Company Details:</strong> HULA CAPITAL LIMITED is a private limited company registered in England & Wales (Company No. 16534250) with registered office at 10th Floor 19 Sun St, London, EC2A 2FJ, United Kingdom.
                </p>
              </div>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-navy-800 mb-3">Personal Information</h3>
              <p className="text-gray-600 mb-4">We may collect the following types of personal information:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Name, email address, phone number, and postal address</li>
                <li>Date of birth and government-issued identification numbers</li>
                <li>Financial information including income, assets, and investment experience</li>
                <li>Employment information and source of funds</li>
                <li>Bank account and payment information</li>
                <li>Investment preferences and risk tolerance</li>
              </ul>

              <h3 className="text-xl font-semibold text-navy-800 mb-3">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website and exit pages</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use your personal information for the following purposes:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Providing financial services and investment management</li>
                <li>Processing transactions and managing your account</li>
                <li>Conducting know-your-customer (KYC) and anti-money laundering (AML) checks</li>
                <li>Complying with legal and regulatory requirements</li>
                <li>Communicating with you about our services</li>
                <li>Improving our website and services</li>
                <li>Marketing our services (with your consent)</li>
                <li>Preventing fraud and ensuring security</li>
              </ul>
            </section>

            {/* Legal Basis for Processing */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Legal Basis for Processing</h2>
              <p className="text-gray-600 mb-4">Under GDPR, we process your personal data based on:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Contract:</strong> To perform our services under your investment agreement</li>
                <li><strong>Legal Obligation:</strong> To comply with financial regulations and AML requirements</li>
                <li><strong>Legitimate Interest:</strong> To improve our services and prevent fraud</li>
                <li><strong>Consent:</strong> For marketing communications and optional services</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Information Sharing and Disclosure</h2>
              <p className="text-gray-600 mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Regulatory authorities and government agencies as required by law</li>
                <li>Third-party service providers who assist in our operations</li>
                <li>Financial institutions for transaction processing</li>
                <li>Professional advisors including lawyers and auditors</li>
                <li>Law enforcement agencies when legally required</li>
              </ul>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> We never sell your personal information to third parties for marketing purposes.
                </p>
              </div>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center">
                <Lock className="h-6 w-6 text-gold-600 mr-2" />
                Data Security
              </h2>
              <p className="text-gray-600 mb-4">We implement appropriate technical and organizational measures to protect your personal information:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>SSL encryption for data transmission</li>
                <li>Secure servers with restricted access</li>
                <li>Regular security assessments and updates</li>
                <li>Employee training on data protection</li>
                <li>Multi-factor authentication for sensitive systems</li>
                <li>Regular backup and disaster recovery procedures</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Your Rights</h2>
              <p className="text-gray-600 mb-4">Under GDPR and UK data protection laws, you have the right to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li><strong>Access:</strong> Request copies of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for processing at any time</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Data Retention</h2>
              <p className="text-gray-600 mb-4">We retain your personal information:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>For the duration of our business relationship</li>
                <li>As required by financial regulations (typically 5-7 years)</li>
                <li>Until you withdraw consent for marketing communications</li>
                <li>As necessary to resolve disputes or enforce agreements</li>
              </ul>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Cookies and Tracking</h2>
              <p className="text-gray-600 mb-4">Our website uses cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Enable essential website functionality</li>
                <li>Remember your preferences and settings</li>
                <li>Analyze website usage and performance</li>
                <li>Provide personalized content and recommendations</li>
              </ul>
              <p className="text-gray-600">
                You can control cookies through your browser settings. However, disabling certain cookies may affect website functionality.
              </p>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-600 mb-4">
                Your personal data may be transferred to and processed in countries outside the UK/EEA. We ensure appropriate safeguards are in place, including:
              </p>
              <ul className="list-disc pl-6 text-gray-600 mb-6">
                <li>Adequacy decisions by the UK government or European Commission</li>
                <li>Standard contractual clauses approved by regulators</li>
                <li>Binding corporate rules for multinational organizations</li>
              </ul>
            </section>

            {/* Changes to Policy */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Changes to This Policy</h2>
              <p className="text-gray-600 mb-4">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting the updated policy on our website and indicating the last updated date.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4 flex items-center">
                <Mail className="h-6 w-6 text-gold-600 mr-2" />
                Contact Us
              </h2>
              <p className="text-gray-600 mb-4">
                If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-navy-900 mb-2">HULA CAPITAL LIMITED</h4>
                    <p className="text-gray-600 text-sm">
                      10th Floor 19 Sun St<br />
                      London, EC2A 2FJ<br />
                      United Kingdom
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <Mail className="h-4 w-4 text-gold-600 mr-2" />
                      <span className="text-gray-600 text-sm">info@hulacapital.com</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gold-600 mr-2" />
                      <span className="text-gray-600 text-sm">+44 20 7946 0958</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Supervisory Authority */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-navy-900 mb-4">Supervisory Authority</h2>
              <p className="text-gray-600 mb-4">
                You have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe we have not handled your personal data appropriately:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Information Commissioner's Office</strong><br />
                  Wycliffe House, Water Lane<br />
                  Wilmslow, Cheshire SK9 5AF<br />
                  Tel: 0303 123 1113<br />
                  Website: ico.org.uk
                </p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage; 